import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

const BANNER_IMAGE =
  'https://roadtechservices.netlify.app/images/mower_banner.jpg';
const DETAIL_IMAGE =
  'https://roadtechservices.netlify.app/images/mobilelawn/mower-repair-1.jpg';
const SIDE_IMAGE =
  'https://roadtechservices.netlify.app/images/mobilelawn/mower-repair-2.jpg';
const CTA_IMAGE =
  'https://roadtechservices.netlify.app/images/mower_banner-850w.jpeg';

const whatWeService = [
  'Push mowers (all brands)',
  'Self-propelled mowers',
  'Riding mowers & zero-turns',
  'Blade sharpening & replacement',
  'Oil changes & filter swaps',
  'Carburetor cleaning & adjustment',
  'Belt & drive system repairs',
  'Starter rope & ignition issues',
  'Seasonal tune-ups & preventative care',
];

const faqs = [
  {
    q: 'Do you repair both push and riding mowers?',
    a: 'Yes. We repair all types of lawn mowers including push, self-propelled, and riding models.',
  },
  {
    q: 'Can you service my mower at home?',
    a: "Absolutely. Our service is 100% mobile, so you don't have to haul heavy equipment to a dealer.",
  },
  {
    q: 'Do you offer same-day repairs?',
    a: 'Yes. If the problem is described correctly and we have the parts on hand, we complete repairs the same day.',
  },
  {
    q: 'What brands do you work on?',
    a: "We're certified in Briggs & Stratton, Stihl, Kohler, and Kawasaki, and service many other brands.",
  },
  {
    q: 'How much does lawn mower repair cost?',
    a: 'Pricing depends on the type of mower and repair needed, but we provide transparent quotes before starting any work.',
  },
];

export default function LawnMowerRepair() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="font-body">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 min-h-[420px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src={BANNER_IMAGE}
            alt="Mobile Lawn Mower Repair"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <span className="inline-block bg-orange-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            Lawn Mowers
          </span>
          <h1 className="font-display text-5xl lg:text-7xl font-bold text-white uppercase tracking-tight">
            Mobile Lawn Mower Repair
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Image */}
            <div className="relative h-[520px] rounded-2xl overflow-hidden sticky top-28">
              <img
                src={DETAIL_IMAGE}
                alt="Lawn mower repair at home"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                <div className="flex items-center gap-2 text-white text-sm font-semibold">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-tools-line text-orange-400" />
                  </div>
                  On-Site Lawn Mower Service
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                Convenient. Certified. On-Site.
              </p>
              <h2 className="font-display text-4xl font-bold uppercase tracking-tight text-neutral-900 mb-5 leading-tight">
                Mobile Lawn Mower Repair by Road Tech Services
              </h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-4">
                At Road Tech Services, we take the hassle out of lawn mower
                repair. No more loading your mower into a truck or trailer — we
                come directly to your home to provide fast, reliable service.
              </p>
              <p className="text-neutral-600 text-base leading-relaxed mb-8">
                As a Master Certified Technician for Briggs &amp; Stratton, and
                certified in Stihl, Kohler, and Kawasaki, we bring years of
                expertise right to your driveway. Most repairs can be completed
                the same day if the issue is described correctly and parts are
                available.
              </p>

              {/* What We Service */}
              <div className="bg-stone-50 rounded-xl p-6 mb-8">
                <p className="text-neutral-900 font-semibold text-xs mb-5 uppercase tracking-wider">
                  What We Service
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {whatWeService.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 text-sm text-neutral-600"
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-orange-500 text-base" />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* How It Works */}
              <div className="mb-8">
                <p className="text-neutral-900 font-semibold text-xs mb-5 uppercase tracking-wider">
                  Our Process
                </p>
                <div className="space-y-4">
                  {[
                    'Schedule a convenient appointment',
                    'We arrive at your home with the right tools & parts',
                    'Diagnose and repair your mower on-site',
                    'Perform tune-ups and preventative maintenance',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-7 h-7 rounded-full bg-orange-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </div>
                      <p className="text-neutral-600 text-sm leading-relaxed pt-1">
                        {step}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Book Lawn Mower Repair
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Side Image Section */}
      <section className="bg-stone-50 py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-4">
                Lawn Mower Repair Made Easy
              </p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900 mb-5 leading-tight">
                Professional Mobile Lawn Mower Services
              </h2>
              <p className="text-neutral-600 text-base leading-relaxed mb-6">
                From small push mowers to large riding mowers, Road Tech
                Services provides expert care wherever you are. We keep your
                lawn care equipment running strong all season long.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Blade sharpening & replacements',
                  'Oil changes & tune-ups',
                  'Carburetor cleaning & adjustments',
                  'Certified in Briggs & Stratton, Stihl, Kohler & Kawasaki',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm text-neutral-600">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-circle-fill text-orange-500 text-base" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                Schedule Repair
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="h-[400px] rounded-2xl overflow-hidden">
              <img
                src={SIDE_IMAGE}
                alt="Mobile mower repair service"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">FAQs</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900">
              Lawn Mower Repair FAQs
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-neutral-900 text-sm pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-orange-600 text-lg`} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-neutral-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="absolute inset-0">
          <img src={CTA_IMAGE} alt="Lawn mower repair" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-5">
            Ready to Get Your Mower Running?
          </h2>
          <p className="text-white/75 text-base leading-relaxed mb-8">
            Road Tech Services provides professional, on-time mower repair
            right at your home. Contact us to schedule.
          </p>
          <Link to="/contact" className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer">
            Schedule Repair
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
